import { LayoutGrid, List, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SearchIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export type AgentsViewMode = 'list' | 'grid'

interface AgentsToolbarProps {
  search: string
  onSearchChange: (value: string) => void
  viewMode: AgentsViewMode
  onViewModeChange: (mode: AgentsViewMode) => void
}

export function AgentsToolbar({ search, onSearchChange, viewMode, onViewModeChange }: AgentsToolbarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="relative w-full max-w-[440px]">
        <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-foreground" />
        <Input
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search by agent name, description or version"
          className="h-10 rounded-lg border-input pl-10 pr-9"
          aria-label="Search agents"
          data-testid="agents-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="ml-auto flex items-center gap-1 rounded-md border border-input bg-toolbar p-1" role="group" aria-label="View mode">
        <Button
          variant={viewMode === 'list' ? 'secondary' : 'ghost'}
          size="icon"
          className={cn('h-10 w-10 rounded border-0', viewMode === 'list' && 'bg-primary text-primary-foreground')}
          onClick={() => onViewModeChange('list')}
          aria-label="Switch to list view"
          aria-pressed={viewMode === 'list'}
          data-testid="agents-view-mode-list"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
          size="icon"
          className={cn('h-10 w-10 rounded border-0', viewMode === 'grid' && 'bg-primary text-primary-foreground')}
          onClick={() => onViewModeChange('grid')}
          aria-label="Switch to grid view"
          aria-pressed={viewMode === 'grid'}
          data-testid="agents-view-mode-grid"
        >
          <LayoutGrid className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
